import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { copy } from "../copy";
import Icon from "../components/Icon";
import { adminApi, type Managed, type Options } from "../admin/api";
import {
  AdminPageHeader,
  Card,
  COVER_TYPES,
  contestBody,
  ContestFields,
  coverOK,
  FILE_TYPES,
  FilePicker,
  sampleOK,
  type ContestFormValues,
} from "../admin/ContestForm";
import MarketPanel from "../admin/MarketPanel";
import { Chip, ghostButton, LoadError, Loading, primaryButton, type Load } from "../admin/ui";
import { putFile } from "../contest/upload";
import { AdminGate } from "./Admin";

type Notice = { kind: "error" | "ok"; text: string } | null;

function formOf(m: Managed): ContestFormValues {
  const c = m.contest;
  return {
    name: c.title,
    description: c.description,
    genre: c.genre,
    challenge: c.challenge,
    duration: c.duration,
    seats: String(c.seats),
    voting: c.voting,
    publish: c.published,
  };
}

// The Control Room's Edit contest page: the contest's details, and its
// prediction market with the house bonus and house backing.
export default function EditContest() {
  return (
    <AdminGate title={copy.admin.editContest.title}>
      <EditContestPage />
    </AdminGate>
  );
}

function EditContestPage() {
  const t = copy.admin.editContest;
  const { id = "" } = useParams();
  const [load, setLoad] = useState<Load<{ managed: Managed; options: Options }>>({ status: "idle" });
  const [form, setForm] = useState<ContestFormValues | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [failed, setFailed] = useState<File[]>([]);
  const [notice, setNotice] = useState<Notice>(null);

  const fetchAll = useCallback(async () => {
    setLoad({ status: "loading" });
    const [managed, options] = await Promise.all([adminApi.managed(id), adminApi.options()]);
    if (!managed.ok || !options.ok) {
      setLoad({ status: "error" });
      if (!managed.ok && managed.status === 404) setNotice({ kind: "error", text: t.notFound });
      return;
    }
    setLoad({ status: "ready", data: { managed: managed.data, options: options.data } });
    setForm(formOf(managed.data));
  }, [id, t.notFound]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const noticeBox = notice && (
    <div
      role={notice.kind === "error" ? "alert" : "status"}
      className={`mt-6 flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-[13px] ${
        notice.kind === "error" ? "border-alert/50 bg-alert/10 text-chrome" : "border-money/40 bg-money/10 text-chrome"
      }`}
    >
      <span>{notice.text}</span>
      <button type="button" onClick={() => setNotice(null)} aria-label={copy.admin.dismiss} className="bf-focusable text-chrome-dim hover:text-chrome">
        <Icon name="close" />
      </button>
    </div>
  );

  if (load.status === "error") {
    return (
      <>
        <AdminPageHeader heading={t.heading} />
        {noticeBox}
        <div className="mt-7 rounded-2xl border border-ground-edge bg-ground-raised p-6">
          <LoadError onRetry={() => void fetchAll()} />
        </div>
      </>
    );
  }
  if (load.status !== "ready" || !form) {
    return (
      <>
        <AdminPageHeader heading={t.heading} />
        <Loading />
      </>
    );
  }

  const { managed, options } = load.data;
  const c = managed.contest;
  const set = <K extends keyof ContestFormValues>(key: K, value: ContestFormValues[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const replace = (m: Managed) => setLoad({ status: "ready", data: { managed: m, options } });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setNotice(null);
    const { publish: _publish, ...edit } = contestBody(form);
    const res = await adminApi.editContest(c.id, edit);
    setSaving(false);
    if (!res.ok) {
      setNotice({ kind: "error", text: res.error });
      return;
    }
    replace(res.data);
    setForm(formOf(res.data));
    setNotice({ kind: "ok", text: t.saved });
  };

  const media = copy.admin.newContest;
  const pending = [...(cover ? [cover] : []), ...files];
  const upload = async (todo: File[]) => {
    const missed: File[] = [];
    for (const file of todo) {
      setUploading(media.uploading(file.name, 0));
      const res = await adminApi.presignUpload(c.id, file.name);
      const ok =
        res.ok &&
        (await putFile(
          res.data.url,
          file,
          res.data.content_type,
          (pct) => setUploading(media.uploading(file.name, pct)),
          res.data.headers,
        ));
      if (!ok) missed.push(file);
    }
    setUploading(null);
    return missed;
  };
  const sendFiles = async () => {
    if (uploading || pending.length === 0) return;
    setNotice(null);
    const missed = await upload(pending);
    setFailed(missed);
    if (missed.length === 0) {
      setCover(null);
      setFiles([]);
      setNotice({ kind: "ok", text: t.uploaded });
    } else {
      setNotice({ kind: "error", text: missed.map((f) => media.uploadFailed(f.name)).join(" ") });
    }
  };

  return (
    <>
      <AdminPageHeader heading={c.title || t.heading} />
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Chip tone={c.published ? "money" : "dim"}>{c.published ? t.published : t.unpublished}</Chip>
        <span className="bf-tabular text-[11px] uppercase tracking-[0.14em] text-chrome-dim">
          {t.taken(c.taken)} / {c.seats}
        </span>
        {c.url && (
          // Go serves the contest page; a full page load.
          <a href={c.url} className={`${ghostButton} py-1.5`}>
            <Icon name="externalLink" className="text-sm" />
            {t.view}
          </a>
        )}
      </div>
      {noticeBox}

      <div className="mt-7 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-8">
        <div className="min-w-0 space-y-6">
        <form onSubmit={(e) => void save(e)} className="space-y-6">
          {!c.editable && c.locked_reason && (
            <p className="rounded-xl border border-ground-edge bg-ground-raised px-4 py-3 text-[13px] text-chrome">{c.locked_reason}</p>
          )}
          <ContestFields form={form} set={set} options={options} showPublish={false} disabled={!c.editable} />
          {c.editable && (
            <button type="submit" disabled={saving} className={`${primaryButton} h-12 w-full text-[12px]`}>
              {saving ? t.saving : t.save}
            </button>
          )}
        </form>
        <Card title={media.media} icon="uploadCloud">
          <FilePicker
            id="edit-cover"
            label={media.cover}
            rules={media.coverRules}
            accept={COVER_TYPES.join(",")}
            files={cover ? [cover] : []}
            onPick={(picked) => {
              const f = picked[0];
              if (!f) return;
              if (!coverOK(f)) {
                setNotice({ kind: "error", text: media.badCover });
                return;
              }
              setNotice(null);
              setCover(f);
            }}
            onRemove={() => setCover(null)}
          />
          <FilePicker
            id="edit-files"
            label={media.files}
            rules={media.filesRules}
            accept={FILE_TYPES.join(",")}
            multiple
            files={files}
            onPick={(picked) => {
              const bad = picked.find((f) => !sampleOK(f));
              if (bad) {
                setNotice({ kind: "error", text: media.badFile(bad.name) });
                return;
              }
              setNotice(null);
              setFiles((prev) => [...prev, ...picked.filter((f) => !prev.some((p) => p.name === f.name))]);
            }}
            onRemove={(name) => setFiles((prev) => prev.filter((f) => f.name !== name))}
          />
          {failed.length > 0 && (
            <button type="button" onClick={() => void sendFiles()} className={ghostButton} disabled={!!uploading}>
              <Icon name="refresh" className="text-sm" />
              {media.retry}
            </button>
          )}
          <button type="button" onClick={() => void sendFiles()} disabled={!!uploading || pending.length === 0} className={`${primaryButton} h-12 w-full text-[12px]`}>
            {uploading ?? t.upload}
          </button>
        </Card>
        </div>
        <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
          <MarketPanel managed={managed} onChange={replace} onNotice={setNotice} />
        </div>
      </div>
    </>
  );
}
